import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizService } from '../services/quizService';
import { QuizQuestion, QuizResponse, WidgetSettings } from '../types/quiz';
import EmailCaptureStep from '../components/EmailCaptureStep';

const QuizFlow: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<QuizResponse[]>([]);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>([]); // Changed to array for multi-select support
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'Armenta Learning Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  // Fetch questions and settings on mount
  useEffect(() => {
    const init = async () => {
      try {
        // Fetch questions
        const fetchedQuestions = await quizService.getQuestions();
        setQuestions(fetchedQuestions);

        // Fetch widget settings
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            school_name: data.school_name || 'Armenta Learning Academy',
            primary_color: data.primary_color || '#4F46E5',
            secondary_color: data.secondary_color || '#7C3AED',
            accent_color: data.accent_color || '#10B981',
            logo_url: data.logo_url
          });
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load quiz questions. Please try again.');
        setLoading(false);
      }
    };

    init();
  }, []);

  // Load Google Analytics (production only)
  useEffect(() => {
    // Only load GA in production (not on localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('🔧 Development mode - Google Analytics disabled');
      return;
    }

    // Inject GA script
    const script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-42R5PVVB4Z';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-42R5PVVB4Z');
    `;
    document.head.appendChild(script2);

    console.log('📊 Google Analytics loaded for quiz flow');

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (document.head.contains(script1)) document.head.removeChild(script1);
      if (document.head.contains(script2)) document.head.removeChild(script2);
    };
  }, []);

  // Calculate progress percentage (14%, 28%, 43%, 57%, 71%, 86%, 100%)
  const progress = questions.length > 0
    ? ((currentQuestionIndex + 1) / questions.length) * 100
    : 0;

  const currentQuestion = questions[currentQuestionIndex];

  // Handle answer selection
  const handleAnswerSelect = (answerId: number) => {
    // Question 7 allows selecting 2 answers (multi-select with checkboxes)
    // Questions 1-6 allow selecting 1 answer (single-select with radio buttons)
    if (currentQuestion.question_number === 7) {
      setSelectedAnswerIds(prev => {
        if (prev.includes(answerId)) {
          // Deselect if already selected
          return prev.filter(id => id !== answerId);
        } else if (prev.length < 2) {
          // Add if less than 2 selected
          return [...prev, answerId];
        } else {
          // Already have 2 selected, don't add more
          return prev;
        }
      });
    } else {
      // Questions 1-6 are single selection
      setSelectedAnswerIds([answerId]);
    }
  };

  // Handle "Next" button click - Single purpose: advance to next question
  const handleNext = () => {
    if (!currentQuestion) return;

    // Validate selection based on question type
    const requiredSelections = currentQuestion.question_number === 7 ? 2 : 1;
    if (selectedAnswerIds.length !== requiredSelections) return;

    // Save the final response(s) (only when Next is clicked)
    // For Q7 (multi-select), create a response for each selected answer
    // For Q1-6 (single-select), create one response
    const newResponses: QuizResponse[] = selectedAnswerIds.map(answerId => ({
      question_id: currentQuestion.id,
      answer_id: answerId
    }));
    setResponses(prev => [...prev, ...newResponses]);

    // Reset for next question
    setSelectedAnswerIds([]);

    // Check if this was the last question
    if (currentQuestionIndex === questions.length - 1) {
      // Show email capture
      setShowEmailCapture(true);
    } else {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  // Handle email capture submission
  const handleEmailSubmit = async (data: {
    parent_name: string;
    child_name: string;
    email: string;
  }) => {
    setSubmitting(true);

    // Track email capture in Google Analytics
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'quiz_email_captured', {
          event_category: 'Quiz',
          event_label: 'Email Captured'
        });
        console.log('📊 GA Event: quiz_email_captured');
      }
    }

    try {
      const result = await quizService.submitQuiz({
        responses,
        parent_name: data.parent_name,
        child_name: data.child_name,
        email: data.email
      });

      // Track quiz completion in Google Analytics
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'quiz_completed', {
            event_category: 'Quiz',
            event_label: 'Quiz Completed',
            profile: result.profile.name
          });
          console.log('📊 GA Event: quiz_completed');
        }
      }

      // Navigate to results page with token
      navigate(`/quiz/results/${result.results_token}`);
    } catch (err: any) {
      alert(err.message || 'Failed to submit quiz. Please try again.');
      setSubmitting(false);
    }
  };

  const primaryColor = settings.primary_color || '#4F46E5';
  const secondaryColor = settings.secondary_color || '#7C3AED';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-lg text-white font-semibold"
            style={{ backgroundColor: primaryColor }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show email capture step
  if (showEmailCapture) {
    return (
      <EmailCaptureStep
        onSubmit={handleEmailSubmit}
        loading={submitting}
        settings={settings}
      />
    );
  }

  // Main quiz interface
  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-4 md:mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs md:text-sm font-semibold text-gray-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
            <span className="text-xs md:text-sm font-semibold text-gray-600">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full h-2 md:h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500 ease-out rounded-full"
              style={{
                width: `${progress}%`,
                backgroundColor: primaryColor
              }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8">
          {/* Question Text */}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-8 leading-tight">
            {currentQuestion?.question_text}
          </h2>

          {/* Answer Options */}
          <div className="space-y-3 md:space-y-4 mb-4 md:mb-8">
            {currentQuestion?.answers.map((answer) => {
              const isSelected = selectedAnswerIds.includes(answer.id);
              const isMultiSelect = currentQuestion.question_number === 7;

              return (
                <label
                  key={answer.id}
                  className={`block p-4 md:p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 shadow-md'
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type={isMultiSelect ? "checkbox" : "radio"}
                      name={isMultiSelect ? undefined : "answer"}
                      value={answer.id}
                      checked={isSelected}
                      onChange={() => handleAnswerSelect(answer.id)}
                      className="w-4 h-4 md:w-5 md:h-5 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-3 md:ml-4 text-base md:text-lg text-gray-800">
                      {answer.answer_text}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={
              currentQuestion.question_number === 7
                ? selectedAnswerIds.length !== 2
                : selectedAnswerIds.length !== 1
            }
            className="w-full py-3 md:py-4 px-6 rounded-xl text-white font-bold text-base md:text-lg transition-all duration-200 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{
              background: (
                currentQuestion.question_number === 7
                  ? selectedAnswerIds.length === 2
                  : selectedAnswerIds.length === 1
              )
                ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
                : '#9CA3AF'
            }}
          >
            {currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next →'}
          </button>
        </div>

        {/* School Branding */}
        <div className="mt-4 md:mt-8 text-center">
          <p className="text-xs md:text-sm text-gray-500">
            Powered by {settings.school_name}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuizFlow;
